import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import DoNotDisturbAltOutlinedIcon from "@mui/icons-material/DoNotDisturbAltOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import PendingActionsOutlinedIcon from "@mui/icons-material/PendingActionsOutlined";
import { Theme, TypographyProps } from "@mui/material";
import { TaskStatus, VirtualEventStatus } from "generated-graphql/graphql";
import { DateTime } from "luxon";

export const isTaskOverdue = ({
  deadline,
  taskStatus,
  now = new Date(),
}: {
  deadline: Date | string | null | undefined;
  taskStatus: TaskStatus | null | undefined;
  now?: Date;
}): boolean => {
  if (!deadline) return false;
  if (taskStatus === TaskStatus.Completed || taskStatus === TaskStatus.Canceled) return false;
  return new Date(deadline) < now;
};

export const getDaysOverdue = (
  deadline: Date | string,
  now: DateTime = DateTime.local()
): number => {
  const due =
    typeof deadline === "string"
      ? DateTime.fromISO(deadline, { zone: "utc" }).startOf("day")
      : DateTime.fromJSDate(deadline, { zone: "utc" }).startOf("day");
  const today = now.startOf("day");
  return Math.max(0, Math.floor(today.diff(due, "days").days));
};

export const getOverdueTooltip = (deadline: Date | string): string => {
  const days = getDaysOverdue(deadline);
  return `This task is overdue by ${days} ${days === 1 ? "day" : "days"}`;
};

export const textColorForTask = ({
  taskStatus,
  status,
}: {
  taskStatus: TaskStatus;
  status: VirtualEventStatus;
}): TypographyProps["color"] => {
  if (status === VirtualEventStatus.Overdue) {
    return "error";
  }

  switch (taskStatus) {
    case TaskStatus.Open:
      return "textSecondary";
    case TaskStatus.InProgress:
      return "warning";
    case TaskStatus.Completed:
      return "textDisabled";
    case TaskStatus.Canceled:
      return "textDisabled";
  }
};

export const calendarIconForTask = ({
  taskStatus,
  status,
}: {
  taskStatus: TaskStatus;
  status: VirtualEventStatus;
}) => {
  if (status === VirtualEventStatus.Overdue) {
    return <ErrorOutlineIcon sx={{ fontSize: 14 }} color="error" />;
  } else return taskStatusToIcon(taskStatus);
};

export const taskStatusToIcon = (status: TaskStatus) => {
  switch (status) {
    case TaskStatus.Open:
      return <CircleOutlinedIcon sx={{ fontSize: 14 }} color="info" />;
    case TaskStatus.InProgress:
      return <PendingActionsOutlinedIcon sx={{ fontSize: 14 }} color="warning" />;
    case TaskStatus.Completed:
      return <CheckCircleOutlineIcon sx={{ fontSize: 14 }} color="success" />;
    case TaskStatus.Canceled:
      return <DoNotDisturbAltOutlinedIcon sx={{ fontSize: 14 }} color="disabled" />;
  }
};

export const taskStatusToChipSx = (status: TaskStatus, theme: Theme) => {
  switch (status) {
    case TaskStatus.Open:
      return {
        fontSize: 12,
        backgroundColor: `${theme.palette.primary.light}20`,
        color: theme.palette.primary.main,
      };
    case TaskStatus.InProgress:
      return {
        fontSize: 12,
        backgroundColor: `${theme.palette.warning.light}20`,
        color: theme.palette.warning.dark,
      };
    case TaskStatus.Completed:
      return {
        fontSize: 12,
        backgroundColor: `${theme.palette.success.light}20`,
        color: theme.palette.success.main,
      };
    case TaskStatus.Canceled:
      return {
        fontSize: 12,
        backgroundColor: `${theme.palette.grey[400]}20`,
        color: theme.palette.text.secondary,
      };
  }
};
